// frontend/src/pages/Landing.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguageStore } from '../store/useLanguageStore';
import { LanguageSelector } from '../components/LanguageSelector';
import {
  Menu, X, ArrowRight, Database, Shield, Globe, Lock,
  FileCheck, RefreshCw, Users, Plug, Brain, Wand2,
  Clock, ChevronRight, Check, Minus, Mail, MapPin,
  Code2, Webhook, Activity, AlertTriangle,
  Search, Bell, Scale, ShieldCheck, ExternalLink, Cpu
} from 'lucide-react';

import logoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import logoHorizontalDark from '../assets/images/datiaFlow_horizontal_FO.jpg';
import logoSquare from '../assets/images/datiaFlow_cuadrado.jpg';

/* ─────────────────────────────────────────────
   CONNECTORS DATA — 29 conectores activos
   Protocolos de conexión separados (no son conectores).
   ───────────────────────────────────────────── */
const CONNECTOR_GROUPS = [
  {
    key: 'db',
    connectors: [
      { name: 'MySQL', slug: 'mysql' },
      { name: 'PostgreSQL', slug: 'postgresql' },
      { name: 'Oracle', slug: 'oracle' },
      { name: 'SQL Server', slug: 'sqlserver' },
      { name: 'MongoDB', slug: 'mongodb' },
      { name: 'MariaDB', slug: 'mariadb' },
      { name: 'SAP HANA', slug: 'sap' }
    ]
  },
  {
    key: 'cloud',
    connectors: [
      { name: 'BigQuery', slug: 'bigquery' },
      { name: 'Azure SQL', slug: 'azure' },
      { name: 'Snowflake', slug: 'snowflake' },
      { name: 'Redshift', slug: 'redshift' }
    ]
  },
  {
    key: 'files',
    connectors: [
      { name: 'CSV', slug: 'csv' },
      { name: 'Excel', slug: 'excel' },
      { name: 'Google Sheets', slug: 'googlesheets' },
      { name: 'JSON', slug: 'json' },
      { name: 'DBF', slug: 'dbf' }
    ]
  },
  {
    key: 'saas',
    connectors: [
      { name: 'Dynamics 365', slug: 'dynamics365' },
      { name: 'Salesforce', slug: 'salesforce' },
      { name: 'HubSpot', slug: 'hubspot' },
      { name: 'Odoo', slug: 'odoo' },
      { name: 'REST API', slug: 'restapi' },
      { name: 'Zoho CRM', slug: 'zohocrm' },
      { name: 'Zoho Books', slug: 'zohobooks' },
      { name: 'Zoho Inventory', slug: 'zohoinventory' },
      { name: 'SuiteCRM', slug: 'suitecrm' },
      { name: 'SugarCRM', slug: 'sugarcrm', beta: true },
      { name: 'NetSuite', slug: 'netsuite', beta: true },
      { name: 'Defontana', slug: 'defontana', beta: true }
    ]
  }
];

/* Protocolos de conexión — mostrados como badges, no como conectores */
const CONNECTION_PROTOCOLS = [
  { name: 'FTP', slug: 'ftp' },
  { name: 'SFTP', slug: 'sftp' },
  { name: 'SSH Tunnel', slug: 'ssh' },
  { name: 'AWS S3', slug: 's3' },
  { name: 'Direct', slug: 'direct' }
];

/* ─────────────────────────────────────────────
   CONNECTOR ICON COMPONENT
   ───────────────────────────────────────────── */
const ConnectorIcon = ({ slug, className = 'w-5 h-5' }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return <Database className={`${className} text-slate-400`} />;
  return (
    <img
      src={`${process.env.PUBLIC_URL}/connectors/${slug}.svg`}
      alt=""
      className={className}
      onError={() => setFailed(true)}
      loading="lazy"
    />
  );
};

/* ─────────────────────────────────────────────
   SMOOTH SCROLL HELPER
   ───────────────────────────────────────────── */
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/* ═════════════════════════════════════════════
   LANDING PAGE COMPONENT
   ═════════════════════════════════════════════ */
export const Landing = () => {
  const { t } = useLanguageStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 768) setMobileMenuOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-white">

      {/* ══════════════════════════════════════════
          SECTION 1 — NAVBAR
          Con links de navegación interna.
          ══════════════════════════════════════════ */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">

            <Link to="/" className="flex-shrink-0">
              <img src={logoHorizontal} alt="DatiaFlow" className="hidden sm:block h-11 w-auto" />
              <img src={logoSquare} alt="DatiaFlow" className="sm:hidden h-11 w-auto" />
            </Link>

            {/* Desktop Nav — links + CTAs */}
            <div className="hidden md:flex items-center gap-1 lg:gap-2">
              <button onClick={() => scrollTo('casos')}
                className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors rounded-lg">
                {t('landing.nav_product')}
              </button>
              <button onClick={() => scrollTo('conectores')}
                className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors rounded-lg">
                {t('landing.nav_connectors')}
              </button>
              <button onClick={() => scrollTo('seguridad')}
                className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors rounded-lg">
                {t('landing.nav_security')}
              </button>
              <button onClick={() => scrollTo('precios')}
                className="px-3 py-2 text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors rounded-lg">
                {t('landing.nav_pricing')}
              </button>

              <div className="w-px h-5 bg-slate-200 mx-1" />
              <LanguageSelector />

              <Link
                to="/login"
                className="px-4 py-2 text-sm font-bold text-slate-600 border-2 border-slate-200 rounded-xl hover:border-blue-200 hover:text-blue-600 transition-all"
              >
                {t('landing.nav_login')}
              </Link>
              <Link
                to="/register?action=Demo"
                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md shadow-blue-200 transition-all"
              >
                {t('landing.nav_demo')}
              </Link>
            </div>

            {/* Mobile */}
            <div className="flex items-center gap-2 md:hidden">
              <LanguageSelector />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-slate-600 hover:text-blue-600 transition-colors"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

      </nav>

      {/* Mobile Menu — fuera del nav para evitar que backdrop-blur atrape el fixed */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-white z-40 overflow-y-auto">
          <div className="flex flex-col items-center gap-3 pt-6 px-6">
            <button onClick={() => { scrollTo('casos'); setMobileMenuOpen(false); }}
              className="w-full text-center px-4 py-3 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
              {t('landing.nav_product')}
            </button>
            <button onClick={() => { scrollTo('conectores'); setMobileMenuOpen(false); }}
              className="w-full text-center px-4 py-3 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
              {t('landing.nav_connectors')}
            </button>
            <button onClick={() => { scrollTo('seguridad'); setMobileMenuOpen(false); }}
              className="w-full text-center px-4 py-3 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
              {t('landing.nav_security')}
            </button>
            <button onClick={() => { scrollTo('precios'); setMobileMenuOpen(false); }}
              className="w-full text-center px-4 py-3 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">
              {t('landing.nav_pricing')}
            </button>
            <div className="w-full border-t border-slate-100 my-1" />
            <Link to="/login" onClick={() => setMobileMenuOpen(false)}
              className="w-full text-center px-4 py-3 text-sm font-bold text-slate-600 border-2 border-slate-200 rounded-xl hover:border-blue-200 transition-all">
              {t('landing.nav_login')}
            </Link>
            <Link to="/register?action=Demo" onClick={() => setMobileMenuOpen(false)}
              className="w-full text-center px-4 py-3 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition-all">
              {t('landing.nav_demo')}
            </Link>
          </div>
        </div>
      )}


      {/* ══════════════════════════════════════════
          SECTION 2 — HERO
          Headline orientado a integración + beneficio.
          ══════════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 via-white to-slate-50 pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-16 sm:pb-20 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-800 leading-tight tracking-tight max-w-4xl mx-auto">
            {t('landing.hero_headline')}
          </h1>
          <p className="mt-6 text-base sm:text-lg lg:text-xl text-slate-500 font-medium max-w-3xl mx-auto leading-relaxed">
            {t('landing.hero_subheadline')}
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register?action=Demo"
              className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white bg-blue-600 rounded-2xl hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-xl transition-all flex items-center justify-center gap-2">
              {t('landing.hero_cta_demo')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/register"
              className="w-full sm:w-auto px-8 py-4 text-base font-bold text-blue-600 bg-blue-50 border-2 border-blue-100 rounded-2xl hover:bg-blue-100 transition-all flex items-center justify-center gap-2">
              {t('landing.hero_cta_trial')}
            </Link>
          </div>

          {/* Micro-stats — capability proof */}
          <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
            <button
              onClick={() => scrollTo('conectores')}
              className="flex items-center gap-2 hover:text-blue-600 transition-colors cursor-pointer"
            >
              <Database className="w-4 h-4 text-blue-600" />
              {t('landing.hero_stat_connectors')}
            </button>
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-600" />
              {t('landing.hero_stat_zdr')}
            </span>
            <span className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-purple-600" />
              {t('landing.hero_stat_transforms')}
            </span>
            <span className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-amber-600" />
              {t('landing.hero_stat_languages')}
            </span>
          </div>

          {/* Product screenshots with tabs */}
          <div className="mt-14 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[
                { id: 'dashboard', icon: '📊' },
                { id: 'mapping', icon: '🧠' },
                { id: 'lineage', icon: '🔗' }
              ].map(({ id, icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${activeTab === id
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-blue-200 hover:text-blue-600'
                    }`}
                >
                  <span className="mr-1.5">{icon}</span>
                  {t(`landing.hero_tab_${id}`)}
                </button>
              ))}
            </div>
            <div className="rounded-2xl border border-slate-200 shadow-lg overflow-hidden bg-white">
              <img
                src={`${process.env.PUBLIC_URL}/screenshots/${activeTab}.png`}
                alt={t(`landing.hero_tab_${activeTab}`)}
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-xs text-slate-400 text-center">
              {t(`landing.hero_tab_${activeTab}_caption`)}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 2B — POR DECISOR
          Mensaje segmentado por rol.
          ══════════════════════════════════════════ */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.persona_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto font-medium">
            {t('landing.persona_subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* CTO */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-sm text-slate-800">{t('landing.persona_cto_role')}</h3>
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{t('landing.persona_cto_area')}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('landing.persona_cto_msg')}</p>
              <Link to="/register?action=Demo" className="text-sm font-bold text-blue-600 flex items-center gap-1 hover:gap-2 transition-all">
                {t('landing.persona_cto_cta')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* CFO */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
                  <Scale className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-sm text-slate-800">{t('landing.persona_cfo_role')}</h3>
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{t('landing.persona_cfo_area')}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('landing.persona_cfo_msg')}</p>
              <Link to="/register?action=Demo" className="text-sm font-bold text-blue-600 flex items-center gap-1 hover:gap-2 transition-all">
                {t('landing.persona_cfo_cta')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Gerente de Operaciones */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center">
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-sm text-slate-800">{t('landing.persona_ops_role')}</h3>
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{t('landing.persona_ops_area')}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('landing.persona_ops_msg')}</p>
              <Link to="/register?action=Demo" className="text-sm font-bold text-blue-600 flex items-center gap-1 hover:gap-2 transition-all">
                {t('landing.persona_ops_cta')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Consultor ERP */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-sm text-slate-800">{t('landing.persona_consultant_role')}</h3>
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{t('landing.persona_consultant_area')}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('landing.persona_consultant_msg')}</p>
              <Link to="/register?action=Demo" className="text-sm font-bold text-blue-600 flex items-center gap-1 hover:gap-2 transition-all">
                {t('landing.persona_consultant_cta')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 3 — CASOS DE USO
          Sincronización primero (revenue engine).
          ══════════════════════════════════════════ */}
      <section id="casos" className="bg-slate-50 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.usecases_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 sm:mb-16 max-w-2xl mx-auto font-medium">
            {t('landing.usecases_subtitle')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Card 1 — Continuous Sync (★ principal) */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-sm border-2 border-blue-100 hover:shadow-md transition-all group flex flex-col ring-1 ring-blue-50">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-5">
                <RefreshCw className="w-6 h-6" />
              </div>
              <span className="inline-block text-[10px] font-black text-blue-600 bg-blue-50 px-3 py-1 rounded-full uppercase tracking-widest mb-3 self-start">
                {t('landing.usecase1_badge')}
              </span>
              <h3 className="text-lg sm:text-xl font-black text-slate-800 mb-3">
                {t('landing.usecase1_title')}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-grow">
                {t('landing.usecase1_desc')}
              </p>
              <Link to="/register?action=Demo"
                className="text-sm font-bold text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                {t('landing.usecase1_cta')}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Card 2 — Migration */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-100 hover:shadow-md hover:border-blue-100 transition-all group flex flex-col">
              <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-5">
                <ArrowRight className="w-6 h-6" />
              </div>
              <span className="inline-block text-[10px] font-black text-amber-600 bg-amber-50 px-3 py-1 rounded-full uppercase tracking-widest mb-3 self-start">
                {t('landing.usecase2_badge')}
              </span>
              <h3 className="text-lg sm:text-xl font-black text-slate-800 mb-3">
                {t('landing.usecase2_title')}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-grow">
                {t('landing.usecase2_desc')}
              </p>
              <Link to="/register?action=Demo"
                className="text-sm font-bold text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                {t('landing.usecase2_cta')}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Card 3 — Data Self-Service */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-100 hover:shadow-md hover:border-blue-100 transition-all group flex flex-col">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center mb-5">
                <Users className="w-6 h-6" />
              </div>
              <span className="inline-block text-[10px] font-black text-green-600 bg-green-50 px-3 py-1 rounded-full uppercase tracking-widest mb-3 self-start">
                {t('landing.usecase3_badge')}
              </span>
              <h3 className="text-lg sm:text-xl font-black text-slate-800 mb-3">
                {t('landing.usecase3_title')}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-grow">
                {t('landing.usecase3_desc')}
              </p>
              <Link to="/register?action=Demo"
                className="text-sm font-bold text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                {t('landing.usecase3_cta')}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 4 — COST COMPARISON
          3 columnas: Scripts | Consultora | DatiaFlow
          Con fila de gobernanza agregada.
          ══════════════════════════════════════════ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.compare_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto font-medium">
            {t('landing.compare_subtitle')}
          </p>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  <th className="text-left py-4 px-4 font-black text-slate-400 text-xs uppercase tracking-widest w-1/4">{t('landing.compare_criteria')}</th>
                  <th className="text-center py-4 px-4 font-black text-slate-400 text-xs uppercase tracking-widest">{t('landing.compare_col_scripts')}</th>
                  <th className="text-center py-4 px-4 font-black text-slate-400 text-xs uppercase tracking-widest">{t('landing.compare_col_consultancy')}</th>
                  <th className="text-center py-4 px-4 font-black text-blue-600 text-xs uppercase tracking-widest bg-blue-50/50 rounded-t-2xl">{t('landing.compare_col_datiaflow')}</th>
                </tr>
              </thead>
              <tbody className="text-slate-600">
                {['cost', 'setup', 'maintenance', 'scalability', 'governance', 'security', 'support'].map((row, i) => (
                  <tr key={row} className={i % 2 === 0 ? 'bg-slate-50/50' : ''}>
                    <td className="py-4 px-4 font-bold text-slate-700">{t(`landing.compare_row_${row}`)}</td>
                    <td className="py-4 px-4 text-center text-slate-500">{t(`landing.compare_scripts_${row}`)}</td>
                    <td className="py-4 px-4 text-center text-slate-500">{t(`landing.compare_consult_${row}`)}</td>
                    <td className="py-4 px-4 text-center font-bold text-blue-700 bg-blue-50/50">{t(`landing.compare_datia_${row}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {['cost', 'setup', 'maintenance', 'scalability', 'governance', 'security', 'support'].map((row) => (
              <div key={row} className="bg-slate-50 rounded-2xl p-4">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">{t(`landing.compare_row_${row}`)}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-slate-400 font-medium flex-shrink-0">{t('landing.compare_col_scripts')}:</span>
                    <span className="text-slate-600 text-right">{t(`landing.compare_scripts_${row}`)}</span>
                  </div>
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-slate-400 font-medium flex-shrink-0">{t('landing.compare_col_consultancy')}:</span>
                    <span className="text-slate-600 text-right">{t(`landing.compare_consult_${row}`)}</span>
                  </div>
                  <div className="flex justify-between items-start gap-2 bg-blue-50 -mx-2 px-2 py-1.5 rounded-xl">
                    <span className="text-blue-600 font-bold flex-shrink-0">DatiaFlow:</span>
                    <span className="text-blue-700 font-bold text-right">{t(`landing.compare_datia_${row}`)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-slate-400 text-center max-w-2xl mx-auto leading-relaxed">
            {t('landing.compare_footnote')}
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 5 — HOW IT WORKS (3 pasos)
          ══════════════════════════════════════════ */}
      <section className="bg-slate-50 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.how_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 sm:mb-16 max-w-2xl mx-auto font-medium">
            {t('landing.how_subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Plug className="w-7 h-7" />
              </div>
              <div className="text-xs font-black text-slate-300 uppercase tracking-widest mb-2">{t('landing.how_step_label')} 1</div>
              <h3 className="text-base font-black text-slate-800 mb-2">{t('landing.how_step1_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.how_step1_desc')}</p>
            </div>
            {/* Step 2 */}
            <div className="text-center">
              <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Brain className="w-7 h-7" />
              </div>
              <div className="text-xs font-black text-slate-300 uppercase tracking-widest mb-2">{t('landing.how_step_label')} 2</div>
              <h3 className="text-base font-black text-slate-800 mb-2">{t('landing.how_step2_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.how_step2_desc')}</p>
            </div>
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-14 h-14 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7" />
              </div>
              <div className="text-xs font-black text-slate-300 uppercase tracking-widest mb-2">{t('landing.how_step_label')} 3</div>
              <h3 className="text-base font-black text-slate-800 mb-2">{t('landing.how_step3_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.how_step3_desc')}</p>
            </div>
          </div>
          
          {/* Zero-effort callout */}
          <div className="mt-10 bg-green-50 border border-green-100 rounded-2xl p-5 sm:p-6 text-center max-w-3xl mx-auto">
            <p className="text-sm text-slate-700 font-medium">
              {t('landing.how_zero_effort')}
            </p>
          </div>

          {/* API + Webhooks callout */}
          <div className="mt-12 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 max-w-3xl mx-auto">
            <div className="flex items-center gap-3 flex-shrink-0">
              <Code2 className="w-5 h-5 text-blue-600" />
              <Webhook className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-sm text-slate-600">
              <span className="font-bold text-slate-800">{t('landing.how_api_title')}</span>{' '}
              {t('landing.how_api_desc')}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 6 — GOBERNANZA DE DATOS ★ NUEVA
          El diferenciador real vs competidores.
          ══════════════════════════════════════════ */}
      <section id="gobernanza" className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.gov_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 sm:mb-16 max-w-2xl mx-auto font-medium leading-relaxed">
            {t('landing.gov_subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Health Score */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="w-10 h-10 bg-green-50 text-green-600 rounded-xl flex items-center justify-center mb-4">
                <Activity className="w-5 h-5" />
              </div>
              <h3 className="font-black text-sm text-slate-800 mb-2">{t('landing.gov_health_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.gov_health_desc')}</p>
            </div>
            {/* Schema Drift */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-4">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h3 className="font-black text-sm text-slate-800 mb-2">{t('landing.gov_drift_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.gov_drift_desc')}</p>
            </div>
            {/* Data Rules */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-4">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="font-black text-sm text-slate-800 mb-2">{t('landing.gov_rules_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.gov_rules_desc')}</p>
            </div>
            {/* Reconciliation */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-4">
                <Scale className="w-5 h-5" />
              </div>
              <h3 className="font-black text-sm text-slate-800 mb-2">{t('landing.gov_reconciliation_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.gov_reconciliation_desc')}</p>
            </div>
            {/* Profiling */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="w-10 h-10 bg-cyan-50 text-cyan-600 rounded-xl flex items-center justify-center mb-4">
                <Search className="w-5 h-5" />
              </div>
              <h3 className="font-black text-sm text-slate-800 mb-2">{t('landing.gov_profiling_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.gov_profiling_desc')}</p>
            </div>
            {/* Alerts */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-blue-100 hover:shadow-sm transition-all">
              <div className="w-10 h-10 bg-rose-50 text-rose-600 rounded-xl flex items-center justify-center mb-4">
                <Bell className="w-5 h-5" />
              </div>
              <h3 className="font-black text-sm text-slate-800 mb-2">{t('landing.gov_alerts_title')}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t('landing.gov_alerts_desc')}</p>
            </div>
          </div>

          <div className="mt-10 bg-blue-50 border border-blue-100 rounded-2xl p-5 sm:p-6 text-center max-w-3xl mx-auto">
            <p className="text-sm text-slate-700 font-medium">
              {t('landing.gov_punchline')}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 7 — CONNECTORS GRID
          29 conectores + protocolos como badges.
          ══════════════════════════════════════════ */}
      <section id="conectores" className="bg-slate-50 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.connectors_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto font-medium">
            {t('landing.connectors_subtitle')}
          </p>

          <div className="space-y-8">
            {CONNECTOR_GROUPS.map(({ key, connectors }) => (
              <div key={key}>
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">
                  {t(`landing.connectors_group_${key}`)}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {connectors.map(({ name, slug, beta }) => (
                    <span
                      key={slug}
                      className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:border-blue-200 hover:bg-blue-50 transition-all"
                    >
                      <ConnectorIcon slug={slug} className="w-5 h-5" />
                      {name}
                      {beta && <span className="text-[9px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md uppercase">Beta</span>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Protocolos de conexión — separados de conectores */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">
              {t('landing.connectors_group_protocols')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {CONNECTION_PROTOCOLS.map(({ name, slug }) => (
                <span key={slug} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-500">
                  <ConnectorIcon slug={slug} className="w-4 h-4" />
                  {name}
                </span>
              ))}
            </div>
          </div>

          <p className="mt-10 text-center text-sm text-slate-500">
            {t('landing.connectors_missing')}{' '}
            <Link to="/register?action=Consult" className="text-blue-600 font-bold hover:underline">
              {t('landing.connectors_contact')}
            </Link>
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 7B — INDUSTRIAL PLANT CALLOUT
          Servicio de consultoría, no SaaS self-serve.
          ══════════════════════════════════════════ */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-10 flex flex-col md:flex-row items-start gap-6">
            <div className="w-14 h-14 bg-amber-400/20 text-amber-400 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Cpu className="w-7 h-7" />
            </div>
            <div className="flex-grow">
              <h2 className="text-lg sm:text-xl font-black text-white mb-3">
                {t('landing.plant_title')}
              </h2>
              <p className="text-sm text-slate-300 leading-relaxed mb-2">
                {t('landing.plant_desc')}
              </p>
              <p className="text-xs text-slate-400 mb-5">
                {t('landing.plant_protocols')}
              </p>
              <Link to="/register?action=Consult"
                className="inline-flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors">
                {t('landing.plant_cta')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 8 — SECURITY
          Sin jerga técnica. Lenguaje de negocio.
          ══════════════════════════════════════════ */}
      <section id="seguridad" className="bg-[#1E3A5F] text-white py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-center mb-4">
            {t('landing.security_title')}
          </h2>
          <p className="text-blue-200 text-center mb-12 sm:mb-16 max-w-2xl mx-auto font-medium">
            {t('landing.security_subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, titleKey: 'zdr_title', descKey: 'zdr_desc' },
              { icon: Lock, titleKey: 'encrypt_title', descKey: 'encrypt_desc' },
              { icon: FileCheck, titleKey: 'audit_title', descKey: 'audit_desc' },
              { icon: Globe, titleKey: 'compliance_title', descKey: 'compliance_desc' }
            ].map(({ icon: Icon, titleKey, descKey }) => (
              <div key={titleKey} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/10">
                <Icon className="w-8 h-8 mx-auto mb-4 text-blue-300" />
                <h3 className="font-black text-sm mb-2">{t(`landing.security_${titleKey}`)}</h3>
                <p className="text-blue-200 text-xs leading-relaxed">{t(`landing.security_${descKey}`)}</p>
              </div>
            ))}
          </div>

          {/* Security Posture Statement */}
          <div className="mt-10 text-center">
            <Link
              to="/register?action=Consult"
              className="inline-flex items-center gap-2 text-sm font-bold text-blue-300 hover:text-white transition-colors"
            >
              {t('landing.security_request')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 9 — IT OBJECTION HANDLER
          ══════════════════════════════════════════ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.objection_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 max-w-3xl mx-auto font-medium leading-relaxed">
            {t('landing.objection_subtitle')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {/* Without DatiaFlow */}
            <div className="bg-slate-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-slate-200">
              <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-5">
                {t('landing.objection_without')}
              </h3>
              <ul className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-500">
                    <Minus className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                    {t(`landing.objection_without_${i}`)}
                  </li>
                ))}
              </ul>
            </div>

            {/* With DatiaFlow */}
            <div className="bg-blue-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-blue-100">
              <h3 className="text-sm font-black text-blue-600 uppercase tracking-widest mb-5">
                {t('landing.objection_with')}
              </h3>
              <ul className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    {t(`landing.objection_with_${i}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-8 text-center text-lg font-black text-slate-800">
            {t('landing.objection_punchline')}
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 10 — PRICING ★ NUEVA
          4 planes con asterisco de early adopter.
          ══════════════════════════════════════════ */}
      <section id="precios" className="bg-slate-50 py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.pricing_title')}
          </h2>
          <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto font-medium">
            {t('landing.pricing_subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-3">
            {/* Plan Free Trial */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 flex flex-col">
              <h3 className="text-sm font-black text-slate-800 mb-1">{t('landing.pricing_free_name')}</h3>
              <p className="text-xs text-slate-400 mb-4">{t('landing.pricing_free_desc')}</p>
              <div className="mb-6">
                <span className="text-3xl font-black text-slate-800">$0</span>
                <span className="text-xs text-slate-400 font-medium ml-1">{t('landing.pricing_free_period')}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-grow">
                {[1, 2, 3, 4, 5].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    {t(`landing.pricing_free_f${i}`)}
                  </li>
                ))}
              </ul>
              <Link to="/register"
                className="w-full py-2.5 text-sm font-bold text-blue-600 border-2 border-blue-100 rounded-xl text-center hover:bg-blue-50 transition-all">
                {t('landing.pricing_free_cta')}
              </Link>
            </div>

            {/* Plan Lite */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 flex flex-col">
              <h3 className="text-sm font-black text-slate-800 mb-1">{t('landing.pricing_lite_name')}</h3>
              <p className="text-xs text-slate-400 mb-4">{t('landing.pricing_lite_desc')}</p>
              <div className="mb-6">
                <span className="text-3xl font-black text-slate-800">$79</span>
                <span className="text-sm text-slate-400 font-medium"> /{t('landing.pricing_per_month')}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-grow">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    {t(`landing.pricing_lite_f${i}`)}
                  </li>
                ))}
              </ul>
              <Link to="/register"
                className="w-full py-2.5 text-sm font-bold text-blue-600 border-2 border-blue-100 rounded-xl text-center hover:bg-blue-50 transition-all">
                {t('landing.pricing_lite_cta')}
              </Link>
            </div>

            {/* Plan Starter — highlighted (Recomendado) */}
            <div className="bg-blue-600 rounded-2xl p-5 border-2 border-blue-600 flex flex-col text-white relative shadow-lg shadow-blue-200">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-black bg-amber-400 text-amber-900 px-3 py-1 rounded-full uppercase tracking-widest whitespace-nowrap">
                {t('landing.pricing_popular')}
              </span>
              <h3 className="text-sm font-black mb-1">{t('landing.pricing_starter_name')}</h3>
              <p className="text-xs text-blue-200 mb-4">{t('landing.pricing_starter_desc')}</p>
              <div className="mb-6">
                <span className="text-3xl font-black">$149</span>
                <span className="text-sm text-blue-200 font-medium"> /{t('landing.pricing_per_month')}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-grow">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-blue-100">
                    <Check className="w-3.5 h-3.5 text-blue-300 flex-shrink-0 mt-0.5" />
                    {t(`landing.pricing_starter_f${i}`)}
                  </li>
                ))}
              </ul>
              <Link to="/register"
                className="w-full py-2.5 text-sm font-bold text-blue-600 bg-white rounded-xl text-center hover:bg-blue-50 transition-all">
                {t('landing.pricing_starter_cta')}
              </Link>
            </div>

            {/* Plan Business */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 flex flex-col">
              <h3 className="text-sm font-black text-slate-800 mb-1">{t('landing.pricing_business_name')}</h3>
              <p className="text-xs text-slate-400 mb-4">{t('landing.pricing_business_desc')}</p>
              <div className="mb-6">
                <span className="text-3xl font-black text-slate-800">$499</span>
                <span className="text-sm text-slate-400 font-medium"> /{t('landing.pricing_per_month')}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-grow">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    {t(`landing.pricing_business_f${i}`)}
                  </li>
                ))}
              </ul>
              <Link to="/register?action=Demo"
                className="w-full py-2.5 text-sm font-bold text-blue-600 border-2 border-blue-100 rounded-xl text-center hover:bg-blue-50 transition-all">
                {t('landing.pricing_business_cta')}
              </Link>
            </div>

            {/* Plan Enterprise */}
            <div className="bg-white rounded-2xl p-5 border border-slate-200 flex flex-col">
              <h3 className="text-sm font-black text-slate-800 mb-1">{t('landing.pricing_enterprise_name')}</h3>
              <p className="text-xs text-slate-400 mb-4">{t('landing.pricing_enterprise_desc')}</p>
              <div className="mb-6">
                <span className="text-xl font-black text-slate-800">{t('landing.pricing_enterprise_price')}</span>
              </div>
              <ul className="space-y-2.5 mb-6 flex-grow">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
                    <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    {t(`landing.pricing_enterprise_f${i}`)}
                  </li>
                ))}
              </ul>
              <Link to="/register?action=Consult"
                className="w-full py-2.5 text-sm font-bold text-blue-600 border-2 border-blue-100 rounded-xl text-center hover:bg-blue-50 transition-all">
                {t('landing.pricing_enterprise_cta')}
              </Link>
            </div>
          </div>

          {/* ── Comparison Table ── */}
          <div className="mt-12 overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-3 font-black text-slate-400 uppercase tracking-widest w-[15%]" />
                  <th className="text-center py-3 px-2 font-bold text-slate-500">{t('landing.pricing_free_name')}</th>
                  <th className="text-center py-3 px-2 font-bold text-slate-500">{t('landing.pricing_lite_name')}</th>
                  <th className="text-center py-3 px-2 font-bold text-blue-600 bg-blue-50/60 rounded-t-xl">{t('landing.pricing_starter_name')}</th>
                  <th className="text-center py-3 px-2 font-bold text-slate-500">{t('landing.pricing_business_name')}</th>
                  <th className="text-center py-3 px-2 font-bold text-slate-500">{t('landing.pricing_enterprise_name')}</th>
                </tr>
              </thead>
              <tbody className="text-slate-600">
                {['price', 'records', 'pipelines', 'users', 'executions', 'governance', 'scripts', 'support'].map((row, i) => (
                  <tr key={row} className={i % 2 === 0 ? 'bg-slate-50/50' : ''}>
                    <td className="py-3 px-3 font-bold text-slate-700">{t(`landing.ptable_row_${row}`)}</td>
                    <td className="py-3 px-2 text-center text-slate-500">{t(`landing.ptable_free_${row}`)}</td>
                    <td className="py-3 px-2 text-center text-slate-500">{t(`landing.ptable_lite_${row}`)}</td>
                    <td className="py-3 px-2 text-center font-bold text-blue-700 bg-blue-50/60">{t(`landing.ptable_starter_${row}`)}</td>
                    <td className="py-3 px-2 text-center text-slate-500">{t(`landing.ptable_business_${row}`)}</td>
                    <td className="py-3 px-2 text-center text-slate-500">{t(`landing.ptable_enterprise_${row}`)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Asterisco + comparación Fivetran */}
          <p className="mt-8 text-xs text-slate-400 text-center max-w-3xl mx-auto leading-relaxed">
            {t('landing.pricing_footnote')}
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 11 — CTA FINAL
          ══════════════════════════════════════════ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 text-center mb-4">
            {t('landing.cta_title')}
          </h2>
          <p className="text-slate-500 text-center mb-10 max-w-2xl mx-auto font-medium">
            {t('landing.cta_subtitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <Link to="/register?action=Demo"
              className="bg-blue-600 text-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-xl transition-all group">
              <h3 className="text-lg font-black mb-2">{t('landing.cta_demo_title')}</h3>
              <p className="text-blue-100 text-sm leading-relaxed mb-4">{t('landing.cta_demo_desc')}</p>
              <span className="text-sm font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
                {t('landing.cta_demo_btn')}
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            <Link to="/register"
              className="bg-white border-2 border-slate-200 text-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:border-blue-200 hover:shadow-md transition-all group">
              <h3 className="text-lg font-black mb-2">{t('landing.cta_trial_title')}</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-4">{t('landing.cta_trial_desc')}</p>
              <span className="text-sm font-bold text-blue-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                {t('landing.cta_trial_btn')}
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            {t('landing.cta_question')}{' '}
            <Link to="/register?action=Consult" className="text-blue-600 font-bold hover:underline inline-flex items-center gap-1">
              {t('landing.cta_question_link')}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 12 — FOOTER
          ══════════════════════════════════════════ */}
      <footer className="bg-slate-900 text-white pt-12 sm:pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 pb-10 border-b border-slate-800">

            {/* Brand */}
            <div>
              <img src={logoHorizontalDark} alt="DatiaFlow" className="h-8 w-auto mb-4" />
              <p className="text-sm text-slate-400 leading-relaxed">
                {t('landing.footer_tagline')}
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">{t('landing.footer_product')}</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollTo('casos')} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t('landing.nav_product')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo('gobernanza')} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t('landing.footer_governance')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo('conectores')} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t('landing.footer_connectors')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo('seguridad')} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t('landing.footer_security')}
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollTo('precios')} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t('landing.nav_pricing')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">{t('landing.footer_legal')}</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">{t('landing.footer_privacy')}</Link></li>
                <li><Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors">{t('landing.footer_terms')}</Link></li>
                <li><Link to="/cookies" className="text-sm text-slate-400 hover:text-white transition-colors">{t('landing.footer_cookies')}</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">{t('landing.footer_contact')}</h4>
              <a href="mailto:support@varanext.com"
                className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4" />
                support@varanext.com
              </a>
              <a href="https://varanext.com" target="_blank" rel="noopener noreferrer"
                className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                varanext.com
              </a>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} Varanext. {t('landing.footer_rights')}
            </p>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Lima, Perú 🇵🇪
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};
